import { ChangeDetectorRef, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable } from 'rxjs';

const STORAGE_KEY = 'monitor-pinned-trips';
const VIEW_STATE_KEY = 'monitor-pinned-trips-view-state';
const MAX_PINNED_TRIPS = 20;

export interface PinnedTripsState {
  tripIds: number[];
  lastUpdated: Date;
}

export interface PinnedTripsViewState {
  tripIds: number[];
  showOnlyPinned: boolean;
  lastUpdated: Date;
}

@Injectable({
  providedIn: 'root'
})
export class PinnedTripsService {
  private pinnedTripsSubject = new BehaviorSubject<Set<number>>(new Set());
  public pinnedTrips$ = this.pinnedTripsSubject.asObservable();

  constructor( private snackBar: MatSnackBar) {
    this.loadFromLocalStorage();
  }

  /**
   * Carga los viajes pineados desde localStorage
   */
  private loadFromLocalStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const state: PinnedTripsState = JSON.parse(stored);
        const tripIds = new Set(state.tripIds);
        this.pinnedTripsSubject.next(tripIds);
      }
    } catch (error) {
      this.pinnedTripsSubject.next(new Set());
    }
  }

  /**
   * Guarda los viajes pineados en localStorage
   */
  private saveToLocalStorage(tripIds: Set<number>): void {
    try {
      const state: PinnedTripsState = {
        tripIds: Array.from(tripIds),
        lastUpdated: new Date()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      this.snackBar.open('Error al guardar los viajes pineados', 'Cerrar', {
        duration: 5000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
      // Falló el guardado en localStorage
    }
  }

  /**
   * Obtiene los IDs de los viajes pineados
   */
  getPinnedTripIds(): number[] {
    return Array.from(this.pinnedTripsSubject.value);
  }

  /**
   * Obtiene el Set de viajes pineados
   */
  getPinnedTripIdsSet(): Set<number> {
    return new Set(this.pinnedTripsSubject.value);
  }

  /**
   * Verifica si un viaje está pineado
   */
  isTripPinned(tripId: number): boolean {
    return this.pinnedTripsSubject.value.has(tripId);
  }

  /**
   * Agrega un viaje a los pineados
   */
  pinTrip(tripId: number): boolean {
    const currentPinned = new Set(this.pinnedTripsSubject.value);

    if (currentPinned.size >= MAX_PINNED_TRIPS && !currentPinned.has(tripId)) {
      return false;
    }

    if (!currentPinned.has(tripId)) {
      currentPinned.add(tripId);
      this.pinnedTripsSubject.next(currentPinned);
      this.saveToLocalStorage(currentPinned);
      return true;
    }

    return false;
  }

  /**
   * Quita un viaje de los pineados
   */
  unpinTrip(tripId: number): boolean {
    const currentPinned = new Set(this.pinnedTripsSubject.value);

    if (currentPinned.has(tripId)) {
      currentPinned.delete(tripId);
      this.pinnedTripsSubject.next(currentPinned);
      this.saveToLocalStorage(currentPinned);
      return true;
    }

    return false;
  }

  /**
   * Alterna el estado de pineado de un viaje
   */
  togglePinTrip(tripId: number): boolean {
    if (this.isTripPinned(tripId)) {
      return this.unpinTrip(tripId);
    } else {
      return this.pinTrip(tripId);
    }
  }

  /**
   * Limpia todos los viajes pineados
   */
  clearAllPinnedTrips(): void {
    this.pinnedTripsSubject.next(new Set());
    this.saveToLocalStorage(new Set());
  }

  /**
   * Obtiene el número de viajes pineados
   */
  getPinnedCount(): number {
    return this.pinnedTripsSubject.value.size;
  }

  /**
   * Verifica si se ha alcanzado el límite de viajes pineados
   */
  hasReachedLimit(): boolean {
    return this.pinnedTripsSubject.value.size >= MAX_PINNED_TRIPS;
  }

  // Métodos para gestión del estado de visualización

  /**
   * Obtiene el estado de visualización desde localStorage
   */
  getViewMode(): boolean {
    try {
      const stored = localStorage.getItem(VIEW_STATE_KEY);
      if (stored) {
        const viewState: PinnedTripsViewState = JSON.parse(stored);
        return viewState.showOnlyPinned;
      }
      return false; // Default: mostrar todos
    } catch (error) {
      this.snackBar.open('Error al cargar el estado de visualización', 'Cerrar', {
        duration: 5000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
      return false;
    }
  }

  /**
   * Guarda el estado de visualización en localStorage
   */
  setViewMode(showOnlyPinned: boolean): void {
    try {
      const viewState: PinnedTripsViewState = {
        tripIds: Array.from(this.pinnedTripsSubject.value),
        showOnlyPinned: showOnlyPinned,
        lastUpdated: new Date()
      };
      localStorage.setItem(VIEW_STATE_KEY, JSON.stringify(viewState));
    } catch (error) {
      this.snackBar.open('Error al guardar el estado de visualización', 'Cerrar', {
        duration: 5000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
      // Falló el guardado en localStorage
    }
  }

  /**
   * Carga el estado completo desde localStorage
   */
  loadViewState(): PinnedTripsViewState {
    try {
      const stored = localStorage.getItem(VIEW_STATE_KEY);
      if (stored) {
        const viewState: PinnedTripsViewState = JSON.parse(stored);
        return viewState;
      }
    } catch (error) {
      this.snackBar.open('Error al cargar el estado de visualización', 'Cerrar', {
        duration: 5000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
      // Error loading view state
    }

    // Estado por defecto
    return {
      tripIds: [],
      showOnlyPinned: false,
      lastUpdated: new Date()
    };
  }

  /**
   * Guarda el estado completo en localStorage
   */
  saveViewState(showOnlyPinned: boolean): void {
    this.setViewMode(showOnlyPinned);
  }

  /**
   * Limpia el estado de visualización
   */
  clearViewState(): void {
    try {
      localStorage.removeItem(VIEW_STATE_KEY);
    } catch (error) {
      this.snackBar.open('Error al limpiar el estado de visualización', 'Cerrar', {
        duration: 5000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
      // Error clearing view state
    }
  }

  get showOnlyPinned(): boolean {
    return this.getViewMode();
  }
}